import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '@/utils/auth-pb';
import { checkEmailTypo } from '@/utils/email-typo-check';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AuthInput } from '@/components/ui/auth-input';
import { Mail, CheckCircle } from 'lucide-react';
import LocalizedLink from '@/components/shared/LocalizedLink';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const emailTypoHint = checkEmailTypo(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setIsSuccess(true);
    } catch (err) {
      setError((err as { message: string }).message || t('forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthLayout title={t('forgotPassword.successTitle')}>
        <div className="flex flex-col items-center py-8">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f8e8]">
            <CheckCircle className="h-8 w-8 text-[#4caf50]" />
          </div>
          <p className="mb-2 text-center text-[16px] text-[#565858]">
            {t('forgotPassword.successMessage')}
          </p>
          <p className="mb-4 text-center text-[16px] font-semibold text-[#0d0d0d]">
            {email}
          </p>
          <p className="mb-8 text-center text-[14px] text-[#565858]">
            {t('forgotPassword.checkSpam')}
          </p>

          <button
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="mb-4 h-[52px] w-full rounded-[12px] border-2 border-[#2197f1] bg-white text-[16px] font-semibold text-[#2197f1] transition-colors hover:bg-[#f0f8ff]"
          >
            {t('forgotPassword.resend')}
          </button>

          <LocalizedLink
            to="/login"
            className="text-[14px] text-[#2197f1] hover:underline"
          >
            {t('forgotPassword.backToLogin')}
          </LocalizedLink>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('forgotPassword.title')}>
      <div className="flex flex-col items-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#e8f4fd]">
          <Mail className="h-8 w-8 text-[#2197f1]" />
        </div>
        <p className="mb-8 text-center text-[16px] text-[#565858]">
          {t('forgotPassword.description')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="rounded-[12px] bg-[#fff0f0] p-4 text-[14px] text-[#ff5b63]">
            {error}
          </div>
        )}

        <AuthInput
          id="email"
          type="email"
          label={t('forgotPassword.emailLabel')}
          placeholder={t('forgotPassword.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        {emailTypoHint && (
          <p className="text-[12px] text-[#f59e0b]">{t('emailTypoCheck.suggestion', { suggestion: emailTypoHint, ns: 'common' })}</p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="mt-6 h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </button>

        <p className="pt-4 text-center">
          <LocalizedLink
            to="/login"
            className="text-[14px] text-[#2197f1] hover:underline"
          >
            {t('forgotPassword.backToLogin')}
          </LocalizedLink>
        </p>
      </form>
    </AuthLayout>
  );
}
